import get from 'lodash/get';
import queryString from 'query-string';


import fetchQuery from 'rebook/apps/utils/fetchQuery';
import * as Error404Actions from 'rebook/apps/base/actions/Error404Actions';
import * as SeoActions from 'rebook/apps/base/actions/SeoActions';
import * as PlayerActions from 'rebook/apps/base/actions/PlayerActions';
import {
  BOOKS_BOOK_DETAIL_SET_DATA,
  BOOKS_BOOK_DETAIL_RESET_DATA,
} from 'rebook/apps/books/constants';
import cropStorageImage from 'rebook/apps/utils/urls/cropStorageImage';


export function fetchInitialData(match, location = {}) {
  return async(dispatch, getState) => {
    const { bookSlug } = match.params;
    const currentState = getState().bookDetail;
    let newState = {
      bookSlug,
    };

    if (location && location.state) {
      newState = {
        ...newState,
        ...location.state,
      };
    }

    if (currentState.book) {
      newState = {
        ...newState,
        ...currentState,
      };
    }

    dispatch(setData(newState));

    // list contexts do not expose full book objects,
    // so we have to refetch the book detail object even if we have a part of it
    if (!newState.book || newState.book.annotation === undefined) {
      // wait for the book detail because other blocks depend on it
      await dispatch(fetchDetail(match));
    }

    await dispatch(fetchMissingBlocks());
  };
}

export function fetchDetail(match) {
  return async(dispatch, getState) => {
    const { ssr, player } = getState();
    const { bookSlug } = match.params;
    const url = `/api/books/s-${bookSlug}/`;

    return fetchQuery(url, { apiVersion: 4, headers: ssr.headers })
      .then(({ data }) => {
        if (!player.bookId || data.available_for_user !== player.availableForUser) {
          dispatch(PlayerActions.setBookData(data));
        }
        dispatch(setData({ book: data }));
      })
      .catch(({ data, resp }) => {
        const status = get(resp, 'status', null);
        if (status === 404) {
          dispatch(Error404Actions.show());
        }
        else {
          Raven.captureMessage('failed to load book detail due to server error', {
            level: 'error',
            extra: {
              status,
              response: data,
              error,
            },
          });
          // throw an exception if fetch detail fails
          // this data is required for rendering the book detail both client and server side
          throw new Error('failed to load book detail due to server error');
        }
      });
  };
}

export function fetchMissingBlocks() {
  return async(dispatch, getState) => {
    const currentState = getState().bookDetail;

    if (!currentState.book) {
      return;
    }

    let promises = [];
    // fetch the other author's books

    if (currentState.book.authors.length & !currentState.authorBooks) {
      promises.push(dispatch(fetchAuthorBooks(currentState.book)));
    }
    if (currentState.book.authors.length & !currentState.authorAudioBooks) {
      promises.push(dispatch(fetchAuthorAudioBooks(currentState.book)));
    }
    if (currentState.book.connected_book && !get(currentState, 'connectedBook.absolute_url', null)) {
      promises.push(dispatch(fetchConnectedBook(currentState.book)));
    }
    if (!currentState.reviews) {
      promises.push(dispatch(fetchBookReviews(currentState.book)));
    }
    if (!currentState.citations) {
      promises.push(dispatch(fetchBookCitations(currentState.book)));
    }
    if (!currentState.editorBooksets) {
      promises.push(dispatch(fetchEditorBookBooksets(currentState.book)));
    }
    if (!currentState.userBooksets) {
      promises.push(dispatch(fetchUserBookBooksets(currentState.book)));
    }
    if (!currentState.seriesList) {
      promises.push(dispatch(fetchSeriesBooks(currentState.book)));
    }
    if (!currentState.userBookshelfList) {
      promises.push(dispatch(fetchBookUserBookshelf(currentState.book)));
    }
    if (!currentState.similarBooks) {
      if (currentState.book.type === 'text') {
        promises.push(dispatch(fetchSimilarBooks(currentState.book)));
      }
    }
    await (promises.length > 0 ? Promise.all(promises) : Promise.resolve(null));
  };
}

export function fetchConnectedBook(book) {
  return (dispatch, getState) => {
    const { ssr } = getState();
    const connectedBookId = get(book, 'connected_book.id', null);
    if (!connectedBookId) {
      return Promise.resolve();
    }

    return fetchQuery(`/api/books/${connectedBookId}/`, { apiVersion: 4, headers: ssr.headers })
      .then(({ data }) => {
        dispatch(setData({
          connectedBook: data,
        }));
      })
      .catch(({ data, resp }) => {
        // 404 is normal. audiobooks are not available yet
        if (resp.status !== 404) {
          Raven.captureMessage('failed to load book connected book due to server error', {
            level: 'error',
            extra: {
              status: resp ? resp.status : null,
              response: data,
            },
          });
        }
      });
  };
}

export function fetchAuthorBooks(book) {
  return async(dispatch, getState) => {
    const { ssr } = getState();
    let query = {
      authors: book.main_author.id,
      id__ne: book.id,
      limit: 10,
      type: 'text',
    };
    const qs = queryString.stringify(query);
    const url = `/api/books/?${qs}`;
    return await fetchQuery(url, { apiVersion: 4, headers: ssr.headers })
      .then(({ data }) => {
        dispatch(setData({
          authorBooks: data.objects,
        }));
      })
      .catch(({ data, resp }) => {
        Raven.captureMessage('failed to load book author books due to server error', {
          level: 'error',
          extra: {
            status: resp ? resp.status : null,
            response: data,
          },
        });
        // we dont care if this request fails
        // so no exception is raised here
      });
  };
}

export function fetchAuthorAudioBooks(book) {
  return async(dispatch, getState) => {
    const { ssr } = getState();
    let query = {
      authors: book.main_author.id,
      id__ne: book.id,
      limit: 10,
      type: 'audio',
    };
    const qs = queryString.stringify(query);
    const url = `/api/books/?${qs}`;
    return await fetchQuery(url, { apiVersion: 4, headers: ssr.headers })
      .then(({ data }) => {
        dispatch(setData({
          authorAudioBooks: data.objects,
        }));
      })
      .catch(({ data, resp }) => {
        Raven.captureMessage('failed to load book author audio books due to server error', {
          level: 'error',
          extra: {
            status: resp ? resp.status : null,
            response: data,
          },
        });
        // we dont care if this request fails
        // so no exception is raised here
      });
  };
}

export function fetchBookReviews(book) {
  return async(dispatch, getState) => {
    const { ssr } = getState();
    const url = `/api/book-reviews/?book=${book.id}&html=1&limit=3`;  // first 3 reviews
    return await fetchQuery(url, { headers: ssr.headers })
      .then(
        ({ data }) => {
          dispatch(setData({
            reviews: data.objects,
          }));
        }
      )
      .catch(
        ({ data, resp }) => {
          Raven.captureMessage('failed to load reviews for book due to server error', {
            level: 'error',
            extra: {
              status: resp ? resp.status : null,
              response: data,
            },
          });
        }
      );
  };
}

export function fetchBookBooksets(book) {
  return async(dispatch, getState) => {
    const { ssr } = getState();
    const booksetLimit = 5;
    const url = `/api/booksets/?book=${book.id}&booklimit=0&limit=${booksetLimit}`;
    return await fetchQuery(url, { apiVersion: 4, headers: ssr.headers })
      .then(
        ({ data }) => {
          dispatch(setData({
            booksets: {
              objects: data.objects,
              totalCount: data.meta['total_count'],
            },
          }));
        }
      )
      .catch(
        ({ data, resp }) => {
          Raven.captureMessage('failed to load booksets for book due to server error', {
            level: 'error',
            extra: {
              status: resp ? resp.status : null,
              response: data,
            },
          });
        }
      );
  };
}

export function fetchEditorBookBooksets(book) {
  return async(dispatch, getState) => {
    const { ssr } = getState();
    const booksetLimit = 5;
    const url = `/api/booksets/?book=${book.id}&booklimit=0&limit=${booksetLimit}&authored_by=0`;
    return await fetchQuery(url, { apiVersion: 4, headers: ssr.headers })
      .then(
        ({ data }) => {
          dispatch(setData({
            editorBooksets: {
              objects: data.objects,
              totalCount: data.meta['total_count'],
            },
          }));
        }
      )
      .catch(
        ({ data, resp }) => {
          Raven.captureMessage('failed to load booksets for book due to server error', {
            level: 'error',
            extra: {
              status: resp ? resp.status : null,
              response: data,
            },
          });
        }
      );
  };
}

export function fetchUserBookBooksets(book) {
  return async(dispatch, getState) => {
    const { ssr } = getState();
    const booksetLimit = 5;
    const url = `/api/booksets/?book=${book.id}&booklimit=0&limit=${booksetLimit}&authored_by=1`;
    return await fetchQuery(url, { apiVersion: 4, headers: ssr.headers })
      .then(
        ({ data }) => {
          dispatch(setData({
            userBooksets: {
              objects: data.objects,
              totalCount: data.meta['total_count'],
            },
          }));
        }
      )
      .catch(
        ({ data, resp }) => {
          Raven.captureMessage('failed to load public bookshelves for book due to server error', {
            level: 'error',
            extra: {
              status: resp ? resp.status : null,
              response: data,
            },
          });
        }
      );
  };
}

const fetchBooksForSeries = async(series, ssr) => {
  const seriesBooksUrl = `/api/catalog/series/${series.id}/books/?limit=10`;
  return await (
    fetchQuery(seriesBooksUrl, { headers: ssr.headers })
      .then(({ data }) => {
        return data.objects;
      })
      .catch(({ data, resp }) => {
        Raven.captureMessage('failed to load series books for book due to server error', {
          level: 'error',
          extra: {
            status: resp ? resp.status : null,
            response: data,
          },
        });
      })
  );
};

export function fetchSeriesBooks(book) {
  return async(dispatch, getState) => {
    // fetch the list of series objects that the book belongs to
    const { ssr } = getState();
    const seriesListUrl = `/api/catalog/series/?book=${book.id}&limit=2`;
    const bookSeriesList = await (
      fetchQuery(seriesListUrl, { headers: ssr.headers })
        .then(
          ({ data }) => {
            // separately fetch book list for every book series
            return data.objects;
          }
        )
        .catch(
          ({ data, resp }) => {
            Raven.captureMessage('failed to load series list for book due to server error', {
              level: 'error',
              extra: {
                status: resp ? resp.status : null,
                response: data,
              },
            });
            return [];
          }
        )
    );
    let seriesList = [];
    // obtain list of books for each series found for this book
    await Promise.all(
      bookSeriesList.map(
        async(series) => {
          let books;
          try {
            books = await fetchBooksForSeries(series, ssr);
          }
          catch (error) {
            Raven.captureException();
          }
          // skip failed book requests and series without books
          if (books && books.length > 0) {
            seriesList.push({
              series,
              books,
            });
          }
        }
      )
    );
    dispatch(setData({ seriesList }));
  };
};

const sortCitations = (citations) => {
  if (!citations.length) {
    return citations;
  }
  let userCitations = [];
  let result = [];
  for (let i = 0; i < citations.length; i++) {
    const isUserCitation = get(citations[i], 'my_citation', false);
    if (isUserCitation) {
      userCitations.push(citations[i]);
    }
    else {
      result.push(citations[i]);
    }
  }
  userCitations.map((item) => {
    result.unshift(item);
  });
  return result;
};

export function fetchBookCitations(book) {
  return async(dispatch, getState) => {
    const { ssr } = getState();
    const url = `/api/citations/?book=${book.id}&limit=3`; // first 3 citations
    return await fetchQuery(url, { headers: ssr.headers })
      .then(
        ({ data }) => {
          dispatch(setData({
            citations: sortCitations(data.objects),
          }));
        }
      )
      .catch(
        ({ data, resp }) => {
          Raven.captureMessage('failed to load citations for book due to server error', {
            level: 'error',
            extra: {
              status: resp ? resp.status : null,
              response: data,
            },
          });
        }
      );
  };
}

export function fetchBookUserBookshelf(book) {
  return async(dispatch, getState) => {
    const { ssr, user } = getState();

    if (!user) {
      dispatch(setData({
        userBookshelfList: null,
      }));
      return null;
    }

    const url = `/api/bookshelf/?user=${user.id}&book=${book.id}`;
    return await fetchQuery(url, { apiVersion: 5, headers: ssr.headers })
      .then(
        ({ data }) => {
          dispatch(setData({
            userBookshelfList: data.objects,
          }));
        }
      )
      .catch(
        ({ data, resp }) => {
          Raven.captureMessage('failed to load user bookshelf list for book due to server error', {
            level: 'error',
            extra: {
              status: resp ? resp.status : null,
              response: data,
            },
          });
        }
      );
  };
}

export function fetchSimilarBooks(book) {
  return async(dispatch, getState) => {
    const { ssr } = getState();
    const isAudioBook = get(book, 'type', 'text') === 'audio';
    const url = `/api/book-recommend/${book.id}/`;
    /*
     * Пока рекомендатор не научится отдавать
     * похожие аудиокниги, не показываем
     * этот блок
     */
    if (isAudioBook) {
      return null;
    }
    return await fetchQuery(url, { apiVersion: 3, headers: ssr.headers })
      .then(({ data }) => {
        dispatch(setData({
          similarBooks: data.books,
        }));
      })
      .catch(
        ({ data, resp }) => {
          Raven.captureMessage('failed to load similar book list for book due to server error', {
            level: 'error',
            extra: {
              status: resp ? resp.status : null,
              response: data,
            },
          });
        }
      );
  };
}

export function setSeoMeta() {
  return (dispatch, getState) => {
    const { bookDetail, booksPerSubscriptionCounter } = getState();
    const { book } = bookDetail;
    const isAudio = get(book, 'type', 'text') === 'audio';
    const bookSubscriptionPrice = book.web_subscription_full_price;
    const cover200x300 = cropStorageImage(book.default_cover, 200, 300);
    let title = isAudio
      ? `Аудиокнига «${book.name}» — слушать онлайн книгу автора ${book.main_author.cover_name}, `+
        `читает ${get(book, 'main_actor.cover_name', '')}`
      : `«${book.name}» ` +
        `читать${!book.subscription_id ? ' бесплатно' : ''} ` +
        `онлайн книгу${book.main_author ? ` автора ${book.main_author.cover_name}` : '' } ` +
        `на MyBook.ru`
      ;
    let description = isAudio
      ? `«${book.name}» — слушайте аудиокнигу автора ${book.main_author.cover_name},`+
        ` в исполнении ${get(book, 'main_actor.cover_name', '')}`+
        ` на сайте электронной библиотеки MyBook.`+
        ` ${get(book, 'annotation_plain', '')}`

      : `Читать${!book.subscription_id ? ' бесплатно' : ''} онлайн книгу «${book.name}» ` +
        `полностью,${book.main_author ? ` автор ${book.main_author.cover_name}` : '' }` +
        `${book.ean ? ` ISBN: ${book.ean}` : ''}, в электронной библиотеке MyBook.ru. ` +
        `Зарегистрируйтесь и читайте онлайн более ` +
        `${book.subscription_id
          ? booksPerSubscriptionCounter.total
          : booksPerSubscriptionCounter.free} электронных книг!`;
    let ogTitle = `${book.name}${book.main_author ? ` — ${book.main_author.cover_name}` : '' } ` +
      `— ${isAudio ? 'слушать' : 'читать'} книгу онлайн, на iPhone, iPad и Android`;
    let ogDescription = `«${book.name}»` +
      `${book.main_author ? ` и другие книги ${book.main_author.cover_name}` : ''} ` +
      `всего за ${bookSubscriptionPrice} рублей в месяц`;

    // Ставим приоритетные мета-теги из админки
    if (book.seo_meta_title) {
      title = book.seo_meta_title;
    }
    if (book.seo_meta_description) {
      description = book.seo_meta_description;
    }
    if (book.seo_og_title) {
      ogTitle = book.seo_og_title;
    }
    if (book.seo_og_description) {
      ogDescription = book.seo_og_description;
    }

    const desktopUrl = `https://${BASE_HOST}${book.absolute_url}`;
    const ogTags = [
      { name: 'type', value: 'books.book' },
      { name: 'image', value: cover200x300 },
      { name: 'title', value: ogTitle },
      { name: 'description', value: ogDescription },
      { name: 'url', value: desktopUrl },
    ];
    const ogTwitterTags = [
      { name: 'title', value: `${book.name}` +
                              `${book.main_author ? `, ${book.main_author.cover_name}` : '' } на mybook.ru` },
      { name: 'description', value: 'Читайте с компьютера, смартфона или планшета. Даже без интернета' },
      { name: 'image', value: cover200x300 },
    ];
    let ogBookTags = [
      { name: 'rating:value', value: book.rating.rating ? book.rating.rating : 0 },
      { name: 'rating:scale', value: 5 },
    ];
    if (book.ean) {
      ogBookTags.push({
        name: 'isbn',
        value: book.ean,
      });
    }
    if (book.main_author) {
      ogBookTags.push({
        name: 'author',
        value: `https://${BASE_HOST}${book.main_author.absolute_url}`,
      });
    }
    dispatch(SeoActions.setData({
      canonicalUrl: book.seo_meta_canonical ? book.seo_meta_canonical : desktopUrl,
      imageSrc: cover200x300,
      robots: book.seo_meta_robots,
      title,
      description,
      ogTags,
      ogBookTags,
      ogTwitterTags,
    }));
  };
}

export function setData(payload) {
  return (dispatch) => {
    const { book } = payload;
    dispatch({
      type: BOOKS_BOOK_DETAIL_SET_DATA,
      payload,
    });
    // update meta tags every time the detail state is changed
    if (book) {
      dispatch(setSeoMeta());
    }
  };
}

export function resetData() {
  return (dispatch) => {
    dispatch(SeoActions.resetData());
    dispatch({
      type: BOOKS_BOOK_DETAIL_RESET_DATA,
    });
  };
}
